import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { task, hash } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import RouterService from '@ember/routing/router-service';
import { announceRefreshWorkspaces } from '@customerio/hydra-nav-state';
import HydraService from 'cio/services/hydra';
import EnvironmentModel from 'cio/models/environment';
import ApiService from 'cio/services/api';
import isEqual from 'lodash.isequal';
import DS from 'ember-data';
import { action } from '@ember/object';
import AccountService from 'cio/services/account';
import UserModel from 'cio/models/user';

const HYDRA_API_ENVIRONMENT_ATTRIBUTES = ['name', 'hexCode'];

function hasChangedHydraApiWorkspaceAttributes(environment: EnvironmentModel) {
  let changedAttributes = (environment as EnvironmentModel).changedAttributes();

  return HYDRA_API_ENVIRONMENT_ATTRIBUTES.some((attribute) => {
    return (
      changedAttributes[attribute] != null &&
      !isEqual(changedAttributes[attribute]?.[0], changedAttributes[attribute]?.[1])
    );
  });
}

function hasChangedNonHydraApiWorkspaceAttributes(environment: EnvironmentModel) {
  let changedAttributes = (environment as EnvironmentModel).changedAttributes();

  return Object.keys(changedAttributes).some((attribute) => {
    let isHydraApiAttribute = HYDRA_API_ENVIRONMENT_ATTRIBUTES.includes(attribute);

    if (isHydraApiAttribute) {
      return false;
    }

    return !isEqual(changedAttributes[attribute]?.[0], changedAttributes[attribute]?.[1]);
  });
}

export default class WorkspaceSettingsEditController extends Controller {
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service hydra!: HydraService;
  @service router!: RouterService;
  @service store!: DS.Store;
  @service account!: AccountService;

  // Relationships aren't tracked in changedAttributes(), so we need to keep
  // track of changes ourselves
  private initialState: { userIds: string[] } = {
    userIds: [],
  };

  declare model: {
    users: DS.AdapterPopulatedRecordArray<UserModel>;
    hydraUsersPermissions: null | { user_ids: number[] };
  };

  @action
  prepareInitialState() {
    if (!this.account.isJourneysAvailable) {
      // If the user is on a CDP-only account, we need to populate the initial
      // state with the user IDs fetched from the users_permissions endpoint,
      // since the environment model won't have any users attached to it.
      //
      // Subsequent calls to prepareRelationshipState() will use the internal state
      // of the environment model, set up by SettingsEnvironmentForm, but
      // that function is called after this one.
      //
      // TODO: Should investigate if we can avoid this special case.
      this.initialState = {
        userIds: this.model.hydraUsersPermissions?.user_ids?.map?.(String).sort?.() ?? [],
      };
      return;
    }

    this.initialState = this.prepareRelationshipState(this.environment.current as EnvironmentModel);
  }

  private prepareRelationshipState(environment: EnvironmentModel) {
    return {
      userIds: environment.hasMany('users').ids().sort(),
    };
  }

  private hasChangedRelationships() {
    let currentState = this.prepareRelationshipState(this.environment.current as EnvironmentModel);

    return !isEqual(currentState, this.initialState);
  }

  submit = task(async () => {
    let environment = this.environment.current as EnvironmentModel;

    if (this.hydra.isHydraEnabled) {
      let requests: Record<string, Promise<unknown>> = {};

      let updateWorkspaceViaHydraApi = hasChangedHydraApiWorkspaceAttributes(environment);

      let updateWorkspaceViaServicesApi =
        this.account.isJourneysAvailable &&
        (hasChangedNonHydraApiWorkspaceAttributes(environment) || this.hasChangedRelationships());

      if (updateWorkspaceViaHydraApi || this.hasChangedRelationships()) {
        requests.hydra = this.api.put(`workspaces/${environment.id}`, {
          pathScope: 'hydra_account',
          ignoreErrors: true,
          data: {
            name: environment.name,
            color: environment.hexCode,
            users: (environment.users.content as UserModel[]).map((user) => {
              return {
                id: parseInt(user.id),
                role: user.role,
              };
            }),
          },
        });
      }

      // If the account is CDP-only, we need to do a separate request to update
      // the user permissions for the workspace, as this field isn't part of
      // the `workspaces` endpoint.
      //
      // TODO: Should be removed once the relevant backend changes are in place.
      if (!this.account.isJourneysAvailable && this.hasChangedRelationships()) {
        await this.api.put(`users_permissions/${this.environment.lastEnvironmentId}`, {
          pathScope: 'account',
          ignoreErrors: true,
          data: {
            user_ids: this.prepareRelationshipState(environment).userIds.map((value) => parseInt(value)),
          },
        });
      }

      if (updateWorkspaceViaServicesApi) {
        requests.services = environment.save();
      }

      let results = await hash(requests);

      announceRefreshWorkspaces();

      // If we only updated name or color, write to the current environment
      // model to overwrite those properties, so that we don't have to wait for
      // the workspace update to propagate to services (avoids having to poll
      // for the changes)
      if (updateWorkspaceViaHydraApi) {
        this.store.pushPayload({
          environment: {
            id: (results.hydra as any)?.id,
            name: (results.hydra as any)?.name,
            hex_code: (results.hydra as any)?.color,
          },
        });
      }
    } else {
      await environment.save();
    }

    this.alerts.success('environment.edit.success', `Changes to the ${this.environment.current.name} workspace saved`);

    this.router.transitionTo('workspace-settings');

    return;
  });
}
